import { useState } from 'react';
import { Box, Stack, Typography, IconButton, Popover, CircularProgress } from '@mui/material';
import { DropdownIcon } from '../../../../icons';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';

interface RadiusComponentProps {
  onRadiusChange: (value: number) => void;
  isLoading?: boolean;
}

export const RadiusComponent: React.FC<RadiusComponentProps> = ({ onRadiusChange, isLoading = false }) => {
  const theme = useTheme();
  const [selectedRadius, setSelectedRadius] = useState(5);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const radiusOptions = [5, 10, 15, 20, 30, 40, 50];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isLoading) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (value: number) => {
    setSelectedRadius(value);
    onRadiusChange(value);
    handleClose();
  };

  const Option = ({ value }: { value: number }) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={() => handleOptionClick(value)}
      sx={{
        padding: '0.75rem 1rem',
        cursor: 'pointer',
        backgroundColor: selectedRadius === value ? theme.palette.grey[50] : 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.grey[50],
        },
        borderRadius: '8px',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: theme.palette.grey[900],
          fontWeight: 500,
        }}
      >
        {`${value} miles`}
      </Typography>
      {selectedRadius === value && (
        <CheckIcon
          sx={{
            color: theme.palette.success.main,
            fontSize: '1.25rem',
          }}
        />
      )}
    </Stack>
  );

  return (
    <Box>
      <IconButton
        onClick={handleClick}
        disabled={isLoading}
        sx={{
          padding: '0.575rem 0.5rem',
          paddingLeft: '0.75rem',
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: '0.5rem',
          backgroundColor: 'white',
          fontSize: '0.875rem',
          color: theme.palette.grey[900],
          fontWeight: 500,
          minWidth: '100px',
          '&:hover': {
            backgroundColor: isLoading ? 'white' : theme.palette.grey[50],
          },
        }}
      >
        <Stack 
          direction="row" 
          alignItems="center" 
          sx={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        >
          <span>{`${selectedRadius} miles`}</span>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto',
            marginRight: '-0.25rem',
            position: 'relative',
            width: '30px',
            justifyContent: 'center'
          }}>
            {isLoading ? (
              <CircularProgress size={16} sx={{ color: theme.palette.grey[500] }} />
            ) : (
              <DropdownIcon />
            )}
          </Box>
        </Stack>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: '12px',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            minWidth: '240px',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            padding: '1rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: theme.palette.grey[900],
              marginBottom: '0.75rem',
            }}
          >
            Search Radius
          </Typography>
          <Stack spacing={0.5}>
            {radiusOptions.map((radius) => (
              <Option key={radius} value={radius} />
            ))}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default RadiusComponent; 